import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LogOut } from "lucide-react";
import AdminOrders from "@/components/admin/AdminOrders";
import AdminContent from "@/components/admin/AdminContent";
import AdminFormFields from "@/components/admin/AdminFormFields";
import AdminDashboard from "@/components/admin/AdminDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full rounded-xl bg-white border-2 border-ink px-4 py-3 text-sm font-medium focus:outline-none focus:shadow-[3px_3px_0_#FF3B7E] transition-shadow";

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("vv_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("dashboard");
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setChecking(false))
      .catch(() => {
        localStorage.removeItem("vv_admin_token");
        setToken("");
        setChecking(false);
      });
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("vv_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("vv_admin_token");
    setToken("");
  };

  if (token && checking) return <div className="min-h-screen flex items-center justify-center text-plum font-head">Loading…</div>;

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-cream" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl bg-white border-[3px] border-ink shadow-[8px_8px_0_#2D1A32] p-8">
          <div className="flex justify-center mb-4"><img src="/logo.png" alt="Vowyo" className="h-11 w-auto" /></div>
          <p className="text-center text-xs text-lilac font-mono mb-6 uppercase tracking-[0.2em]">Admin Studio</p>
          <label className="block font-head font-bold text-xs text-plum mb-1.5">Email</label>
          <input data-testid="admin-login-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="admin@vowyo.com" required />
          <label className="block font-head font-bold text-xs text-plum mb-1.5 mt-4">Password</label>
          <input data-testid="admin-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" required />
          {error && <p className="mt-3 text-xs font-semibold text-pinkdark" data-testid="admin-login-error">{error}</p>}
          <button data-testid="admin-login-submit" type="submit" className="btn-pop mt-6 w-full rounded-full bg-pink text-white font-head font-bold py-3 border-2 border-ink shadow-[4px_4px_0_#2D1A32]">
            Sign In
          </button>
          <Link to="/" className="block text-center mt-4 text-xs font-mono text-lilac underline underline-offset-4">← Back to store</Link>
        </form>
      </div>
    );
  }

  const tabs = [["dashboard", "Dashboard"], ["orders", "Orders"], ["content", "Page Content"], ["form", "Form Fields"]];
  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b-[3px] border-ink bg-white">
        <div className="w-full max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Vowyo" className="h-7 w-auto" />
            <span className="font-display font-semibold text-lg">Admin Studio</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="font-mono text-[11px] text-lilac underline underline-offset-4">View store</Link>
            <button data-testid="admin-logout-button" onClick={logout} className="btn-pop flex items-center gap-1.5 rounded-full border-2 border-ink bg-cream px-4 py-1.5 text-xs font-head font-bold">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="w-full max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-2.5 mb-8 flex-wrap">
          {tabs.map(([id, label]) => (
            <button key={id} data-testid={`admin-${id}-tab`} onClick={() => setTab(id)}
              className={`btn-pop rounded-full px-5 py-2 text-sm font-head font-bold border-2 border-ink ${tab === id ? "bg-pink text-white shadow-[3px_3px_0_#2D1A32]" : "bg-white text-plum"}`}>
              {label}
            </button>
          ))}
        </div>
        {tab === "dashboard" && <AdminDashboard token={token} />}
        {tab === "orders" && <AdminOrders token={token} />}
        {tab === "content" && <AdminContent token={token} />}
        {tab === "form" && <AdminFormFields token={token} />}
      </div>
    </div>
  );
}

export { API };
export const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
export const toastErr = (e, fallback = "Something went wrong") => toast.error(e.response?.data?.detail || fallback);
